<template>
  <div class="product-mapping-tab">
    <div>
      <h3 class="section-title">Choose your Salesforce object</h3>
      <p class="section-description">
        Choose the object that holds your account product data
      </p>
    </div>
    <BittsCallout
      v-if="feedBlockReason === 'disconnected'"
      size="x-small"
      type="danger"
      data-testid="product-mapping-feed-disconnected"
      title="We're having trouble with your Salesforce account."
    >
      <template #subtitle>
        <p class="product-mapping-tab__callout-subtitle">
          Something is wrong with this connection. See more in the
          <BittsLink
            url="#"
            text="General tab"
            data-testid="product-mapping-feed-disconnected-link"
            @click="goToGeneralTab"
          />.
        </p>
      </template>
    </BittsCallout>
    <BittsCallout
      v-else-if="feedBlockReason === 'paused'"
      size="x-small"
      type="warning"
      data-testid="product-mapping-feed-paused"
      title="Syncing is paused for this data source"
      subtitle="Resume syncing, then come back to map your account products."
    />
    <template v-else>
      <BittsCallout
        size="x-small"
        :type="objectStateCallout.type"
        :data-testid="objectStateCallout.testId"
        :title="objectStateCallout.title"
        :subtitle="objectStateCallout.subtitle"
      />
      <BittsCallout
        v-if="catalogStatus === 'failed'"
        size="x-small"
        type="danger"
        data-testid="product-mapping-catalog-error"
        title="We couldn't load your Salesforce objects"
        subtitle="Please try again in a few moments."
        action-text="Retry"
        @bitts-callout-action="loadCatalog"
      />
      <p
        v-else-if="catalogStatus === 'empty'"
        data-testid="product-mapping-catalog-empty"
        class="section-description"
      >
        We couldn't find any Salesforce objects to map. Check that your
        Salesforce user can see the object that holds your account product data.
      </p>
      <BittsSelect
        v-else
        aria-label="Salesforce object"
        class="product-mapping-tab__object-select"
        data-testid="product-mapping-object-select"
        placeholder="Select a Salesforce object"
        :searchable="true"
        :filter-function="filterObjectOptions"
        :loading="catalogStatus === 'loading'"
        :none-remaining-message="objectNotFoundMessage"
        :disabled="!isEditable"
        :model-value="objectApiName"
        :options="objectOptions"
        @update:model-value="handleObjectChange"
      >
        <template #option="{ option }">
          <div class="product-mapping-tab__object-option">
            <span class="product-mapping-tab__object-option-label">
              {{ option.label }}
            </span>
            <span class="product-mapping-tab__object-option-api-name">
              {{ option.value }}
            </span>
          </div>
        </template>
      </BittsSelect>
    </template>
    <BittsDivider class="my-0" />
    <div>
      <h3 class="section-title">Map product fields</h3>
      <p class="section-description">
        Match fields from your selected object to Crossbeam's product fields.
      </p>
    </div>
    <BittsCallout
      v-if="fieldsCallout"
      size="x-small"
      :type="fieldsCallout.type"
      :data-testid="fieldsCallout.testId"
      :title="fieldsCallout.title"
      :subtitle="fieldsCallout.subtitle"
      :action-text="fieldsCallout.actionText"
      @bitts-callout-action="fieldsCallout.action"
    />
    <div
      v-for="(section, index) in ACCOUNT_PRODUCT_SECTIONS"
      :key="section.title"
      class="product-mapping-tab__section-block"
    >
      <BittsDivider v-if="index" class="mt-0 mb-8" />
      <h4 class="product-mapping-tab__section-title">{{ section.title }}</h4>
      <div class="product-mapping-tab__grid">
        <span class="product-mapping-tab__header-label">CRM fields</span>
        <span aria-hidden="true" />
        <TextWithTooltip
          text="Crossbeam product fields"
          text-classes="text-neutral-text-weak text-sm font-bold"
          tooltip-text="These are the Crossbeam fields your CRM fields will be mapped to."
        />
      </div>
      <div class="product-mapping-tab__rows">
        <ProductMappingRow
          v-for="row in section.rows"
          :key="`${row.mdmProperty}-${selectedObjectApiName}`"
          :row="row"
          :disabled="areRowsDisabled"
          :loading="fieldsStatus === 'loading'"
          :model-value="rowModelValue(row)"
          :options="rowOptions(row)"
          :error-message="rowErrorMessage(row)"
          :no-options-message="rowNoOptionsMessage(row)"
          @update:model-value="handleRowChange(row, $event)"
          @touched="dirtyRows.add(row.mdmProperty)"
        />
      </div>
    </div>
    <Teleport to="body">
      <BittsModal
        v-if="isConfirmVisible"
        class="product-mapping-tab__confirm"
        :class="{
          'product-mapping-tab__confirm--errored': Boolean(saveErrorMessage),
        }"
        variant="confirm"
        :icon="['fak', 'product-type']"
        icon-color="info"
        :title="`Save ${selectedObjectLabel} as your Account Product object?`"
        description="This object cannot be changed once it is saved."
        :action-loading="isSaving"
        primary-button-text="Save mapping"
        secondary-button-text="Cancel"
        @action="handleConfirmSave"
        @closed="closeConfirm"
      >
        <BittsCallout
          v-if="saveErrorMessage"
          size="x-small"
          type="danger"
          data-testid="product-mapping-save-error"
          title="Something went wrong"
          :subtitle="saveErrorMessage"
        />
      </BittsModal>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import {
  BittsCallout,
  BittsDivider,
  BittsLink,
  BittsModal,
  BittsSelect,
} from '@crossbeam/bitts';

import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { DataSourceSettingsTabType } from '@/components/data-sources/SettingsModal/constants';
import TextWithTooltip from '@/components/performance-dashboard/TextWithTooltip.vue';

import {
  CONNECTION_CHECK_ERROR_STATUS,
  SOURCE_STATUS_MAP,
} from '@/constants/data_sources';
import { useFeedsStore, useSourcesStore } from '@/stores';

import {
  type CreateAccountProductSourceResult,
  useAccountProductMappingApi,
} from '../composables/useAccountProductMappingApi';
import { useAccountProductSource } from '../composables/useAccountProductSource';
import { useObjectCatalog } from '../composables/useObjectCatalog';
import { useObjectFields } from '../composables/useObjectFields';
import {
  ACCOUNT_PRODUCT_ROWS,
  ACCOUNT_PRODUCT_SECTIONS,
  MappingRowConfig,
} from '../constants';

import ProductMappingRow from './ProductMappingRow.vue';

type Props = {
  feedId: number;
};

const props = defineProps<Props>();

const [UNAUTHORIZED_SOURCE_STATUS] = SOURCE_STATUS_MAP.missing;

const {
  accountProductSource,
  fieldsByMdmProperty,
  hasSyncedAccountSource,
  disappearedMdmProperties,
} = useAccountProductSource(() => props.feedId);

const sourcesStore = useSourcesStore();

/* The store loads once at construction and never polls, so a tab opened later
   in the session can be looking at a feed that was configured meanwhile. */
onMounted(() => sourcesStore.refreshSourcesStore());

const feedsStore = useFeedsStore();
const feed = computed(() => feedsStore.getFeedById(props.feedId));

type FeedBlockReasonType = 'disconnected' | 'paused';

/* A dead token beats a pause: reconnecting is what the user has to do first. */
const feedBlockReason = computed<FeedBlockReasonType | undefined>(() => {
  if (
    feed.value?.status === CONNECTION_CHECK_ERROR_STATUS ||
    accountProductSource.value?.status === UNAUTHORIZED_SOURCE_STATUS
  ) {
    return 'disconnected';
  }
  return feed.value?.is_paused ? 'paused' : undefined;
});

const route = useRoute();
const router = useRouter();

function goToGeneralTab() {
  const { id, source_id: sourceId } = route.params;
  router.push({
    name: 'data-sources-settings-new',
    params: { id, tab: DataSourceSettingsTabType.GENERAL, source_id: sourceId },
  });
}

const isEditable = computed(() => !accountProductSource.value);

const {
  status: catalogStatus,
  objects: catalogObjects,
  load: loadCatalog,
} = useObjectCatalog(() => props.feedId);

const {
  status: fieldsStatus,
  fields: objectFields,
  load: loadFields,
  reset: resetFields,
} = useObjectFields(() => props.feedId);

type SelectOption = { value: string; label: string };

function byLabelThenApiName(a: SelectOption, b: SelectOption) {
  return a.label.localeCompare(b.label) || a.value.localeCompare(b.value);
}

const selectedObjectApiName = ref<string | undefined>(undefined);

const objectApiName = computed(() =>
  isEditable.value
    ? selectedObjectApiName.value
    : accountProductSource.value?.table,
);

const objectOptions = computed(() => {
  if (!isEditable.value) {
    const apiName = accountProductSource.value?.table;
    return apiName ? [{ value: apiName, label: apiName }] : [];
  }
  return catalogObjects.value
    .map((object) => ({ value: object.api_name, label: object.label }))
    .sort(byLabelThenApiName);
});

/* The API name is shown on every option, so it has to be searchable too. The
   default filter in BittsSelect only reads the label. */
function filterObjectOptions(search: string, option: { value: string }) {
  const term = search.trim().toLowerCase();
  const found = objectOptions.value.find(({ value }) => value === option.value);
  if (!found) return false;
  return (
    found.label.toLowerCase().includes(term) ||
    found.value.toLowerCase().includes(term)
  );
}

function fieldOptionsForRow(row: MappingRowConfig) {
  return objectFields.value
    .filter((field) => field.normalized_data_type === row.dataType)
    .map((field) => ({ value: field.api_name, label: field.label }))
    .sort(byLabelThenApiName);
}

const objectNotFoundMessage = computed(() =>
  catalogStatus.value === 'loading'
    ? 'Loading your Salesforce objects…'
    : 'No results found',
);

/* A required row with nothing to offer makes the whole object a dead end, so
   the other rows stop taking selections that could never be saved. */
const isObjectUnusable = computed(
  () =>
    fieldsStatus.value === 'loaded' &&
    ACCOUNT_PRODUCT_ROWS.some(
      (row) => row.required && !fieldOptionsForRow(row).length,
    ),
);

const areRowsDisabled = computed(() => {
  if (!isEditable.value || feedBlockReason.value) return true;
  if (!selectedObjectApiName.value) return true;
  if (isObjectUnusable.value) return true;
  return fieldsStatus.value !== 'loaded';
});

const selections = ref<Partial<Record<string, string>>>({});
const dirtyRows = ref(new Set<string>());
const isSaving = ref(false);
const saveErrorMessage = ref<string | undefined>(undefined);

function savedFieldOption(mdmProperty: MappingRowConfig['mdmProperty']) {
  const field = fieldsByMdmProperty.value[mdmProperty];
  return field ? [{ value: field.column, label: field.display_name }] : [];
}

function rowOptions(row: MappingRowConfig) {
  return isEditable.value
    ? fieldOptionsForRow(row)
    : savedFieldOption(row.mdmProperty);
}

function rowModelValue(row: MappingRowConfig) {
  return isEditable.value
    ? selections.value[row.mdmProperty]
    : savedFieldOption(row.mdmProperty)[0]?.value;
}

function handleRowChange(row: MappingRowConfig, apiName: string | undefined) {
  selections.value[row.mdmProperty] = apiName;
  dirtyRows.value.add(row.mdmProperty);
  saveErrorMessage.value = undefined;
}

function resetMapping() {
  selections.value = {};
  dirtyRows.value = new Set();
  saveErrorMessage.value = undefined;
}

function duplicateFieldName(row: MappingRowConfig) {
  const apiName = selections.value[row.mdmProperty];
  if (!apiName) return undefined;
  const isMappedTwice = ACCOUNT_PRODUCT_ROWS.some(
    (other) =>
      other.mdmProperty !== row.mdmProperty &&
      selections.value[other.mdmProperty] === apiName,
  );
  if (!isMappedTwice) return undefined;
  const field = objectFields.value.find((one) => one.api_name === apiName);
  return `${field?.label ?? apiName} (${apiName})`;
}

function rowErrorMessage(row: MappingRowConfig) {
  if (!isEditable.value) {
    return disappearedMdmProperties.value.has(row.mdmProperty)
      ? 'This field no longer exists in Salesforce.'
      : undefined;
  }
  const duplicate = duplicateFieldName(row);
  if (duplicate) return `${duplicate} is already mapped to another field`;
  const isBlank = !selections.value[row.mdmProperty];
  if (row.required && isBlank && dirtyRows.value.has(row.mdmProperty)) {
    return 'Choose a field before saving';
  }
  return undefined;
}

/* An empty picker leaves the user guessing, so a row with nothing of its type
   says why rather than opening onto nothing. */
function rowNoOptionsMessage(row: MappingRowConfig) {
  if (!isEditable.value || fieldsStatus.value !== 'loaded') return undefined;
  if (rowOptions(row).length) return undefined;
  return (
    row.noOptionsMessage ??
    `This object has no field Crossbeam can map to ${row.label}.`
  );
}

/* The row selects are keyed by object, so they remount and drop the field the
   user picked for the previous one. Ant keeps that internally while the value
   we pass is undefined. */
function handleObjectChange(apiName: string) {
  selectedObjectApiName.value = apiName;
  resetMapping();
  loadFields(apiName);
}

function handleFieldsRetry() {
  if (selectedObjectApiName.value) loadFields(selectedObjectApiName.value);
}

function handleCatalogRefresh() {
  selectedObjectApiName.value = undefined;
  resetMapping();
  resetFields();
  loadCatalog();
}

const FIELDS_CALLOUTS = {
  failed: {
    type: 'danger',
    testId: 'product-mapping-fields-error',
    title: "We couldn't load this object's fields",
    subtitle: 'Please try again in a few moments.',
    actionText: 'Retry',
    action: handleFieldsRetry,
  },
  stale: {
    type: 'warning',
    testId: 'product-mapping-fields-stale',
    title: 'This object is no longer available',
    subtitle:
      'It changed in Salesforce since the list loaded. Refresh the list and pick another object.',
    actionText: 'Refresh list',
    action: handleCatalogRefresh,
  },
} as const;

const fieldsCallout = computed(() => {
  const status = fieldsStatus.value;
  return status === 'failed' || status === 'stale'
    ? FIELDS_CALLOUTS[status]
    : undefined;
});

const selectedObjectLabel = computed(() => {
  const apiName = objectApiName.value;
  const found = objectOptions.value.find((option) => option.value === apiName);
  return found?.label ?? apiName;
});

const objectStateCallout = computed(() => {
  if (!isEditable.value) {
    return {
      type: 'info' as const,
      testId: 'product-mapping-object-saved',
      title: `Account Product source created from ${selectedObjectLabel.value} object`,
      subtitle: 'Mapped fields are now available in Field Sync',
    };
  }
  if (!hasSyncedAccountSource.value) {
    return {
      type: 'danger' as const,
      testId: 'product-mapping-no-account-source',
      title: 'Sync your Salesforce Account object first',
      subtitle:
        'Crossbeam links account products to accounts through the Account object. Turn it on in Field Sync, then come back to map your products.',
    };
  }
  return {
    type: 'warning' as const,
    testId: 'product-mapping-object-warning',
    title: 'This object cannot be changed after saving',
    subtitle:
      "Pick the Salesforce object that holds your account-level product data. Once you click Save, this object can't be edited.",
  };
});

watch(
  () => isEditable.value && !feedBlockReason.value,
  (canLoadCatalog) => {
    if (canLoadCatalog && catalogStatus.value === 'idle') loadCatalog();
  },
  { immediate: true },
);

const hasPendingChanges = computed(() => {
  if (!isEditable.value || !selectedObjectApiName.value) return false;
  if (!hasSyncedAccountSource.value) return false;
  const areRequiredRowsFilled = ACCOUNT_PRODUCT_ROWS.filter(
    (row) => row.required,
  ).every((row) => Boolean(selections.value[row.mdmProperty]));
  const hasErrors = ACCOUNT_PRODUCT_ROWS.some((row) =>
    Boolean(rowErrorMessage(row)),
  );
  return areRequiredRowsFilled && !hasErrors;
});

const disabledSaveReason = computed(() => {
  if (!isEditable.value) return 'You cannot change your settings once set';
  if (!hasSyncedAccountSource.value) {
    return 'Sync your Salesforce Account object before mapping account products';
  }
  return 'Select your CRM fields before saving';
});

const { createAccountProductSource } = useAccountProductMappingApi();

const CONFLICT_STATUS = 409;

const isConfirmVisible = ref(false);

function closeConfirm() {
  isConfirmVisible.value = false;
  saveErrorMessage.value = undefined;
}

function handleSaveAll() {
  if (!hasPendingChanges.value) return;
  saveErrorMessage.value = undefined;
  isConfirmVisible.value = true;
}

function buildFieldMappings() {
  return ACCOUNT_PRODUCT_ROWS.flatMap((row) => {
    const fieldApiName = selections.value[row.mdmProperty];
    return fieldApiName
      ? [{ field_api_name: fieldApiName, mdm_property: row.mdmProperty }]
      : [];
  });
}

async function applySaveResult(result: CreateAccountProductSourceResult) {
  if (result.status === 200 && result.data) {
    sourcesStore.upsertSource(result.data);
    /* The POST answers with the mapped fields alone, so the sync tree would
       see a source with three columns until the list is fetched again. */
    await sourcesStore.refreshSourcesStore();
    closeConfirm();
    return;
  }
  if (result.status === CONFLICT_STATUS) {
    await sourcesStore.refreshSourcesStore();
    closeConfirm();
    return;
  }
  saveErrorMessage.value =
    result.error?.errors?.[0] ??
    "We couldn't save this mapping. Please try again.";
}

async function handleConfirmSave() {
  const objectName = selectedObjectApiName.value;
  if (!objectName) return;

  isSaving.value = true;
  saveErrorMessage.value = undefined;
  try {
    const result = await createAccountProductSource(props.feedId, {
      object_name: objectName,
      field_mappings: buildFieldMappings(),
    });
    await applySaveResult(result);
  } finally {
    /* The confirm variant has no X button and its close is blocked while this
       is set, so a failed save would trap the user until they reload. */
    isSaving.value = false;
  }
}

defineExpose({
  hasPendingChanges,
  isSaving,
  handleSaveAll,
  disabledSaveReason,
});
</script>

<style lang="pcss" scoped>
@reference "../../../../../css/theme.css";
.product-mapping-tab {
  @apply flex flex-col gap-16;
}

.product-mapping-tab__grid {
  @apply grid grid-cols-[1fr_24px_1fr] gap-16;
}

.product-mapping-tab__section-block {
  @apply flex flex-col gap-8;
}

.product-mapping-tab__section-title {
  @apply text-neutral-text-strong font-bold;
}

.section-title {
  @apply text-neutral-text-strong text-m font-bold mb-4;
}

.section-description {
  @apply text-neutral-text;
}

.product-mapping-tab__callout-subtitle {
  @apply text-neutral-text text-sm;
}

.product-mapping-tab__header-label {
  @apply text-neutral-text-weak text-sm font-bold;
}

/* The confirm variant hides its content slot, so the failure callout needs it
   back, and only while there is one to show. */
.product-mapping-tab__confirm--errored {
  &.o-bitts-modal.bitts-modal--confirm {
    /* The header's padding and the description's margin both assume nothing
       follows them, which leaves a gap once the callout does. */
    :deep(.o-bitts-modal__header) {
      @apply pb-0;
    }

    :deep(.o-bitts-modal__description) {
      @apply mb-16;
    }

    :deep(.o-bitts-modal__content) {
      @apply block px-24 pb-24;
    }
  }
}

/* The select starts as wide as the CRM field column below it, then grows for a
   long object name rather than cutting it off. */
.product-mapping-tab__object-select {
  @apply w-fit min-w-[calc(50%-28px)] max-w-full;
}

.product-mapping-tab__object-option {
  @apply flex items-baseline justify-between gap-8 w-full min-w-0;
}

/* Salesforce object names run long, so both halves truncate rather than
   widening the select past its column. The API name gives way first. */
.product-mapping-tab__object-option-label {
  @apply truncate;
}

.product-mapping-tab__object-option-api-name {
  @apply text-neutral-text-weak text-sm truncate shrink-[2];
}

.product-mapping-tab__rows {
  @apply flex flex-col gap-8;
}
</style>
